'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useDeferredValue, useState, type ReactNode } from 'react';

import { cartKeys } from '@/entities/cart';
import { cartQueryOptions } from '@/entities/cart/client';
import { pickupPointLabel, pickupPointMeta } from '@/entities/delivery';
import { deliveryOptionsQueryOptions, pickupPointsQueryOptions } from '@/entities/delivery/client';
import type { PaymentMethodId } from '@/entities/order';
import { placeOrder } from '@/entities/order/client';
import type { PaymentMethod } from '@/entities/payment';
import { DeliveryStep, type DeliveryDraft } from '@/features/checkout-delivery/client';
import { PaymentStep } from '@/features/checkout-payment/client';
import {
  RecipientStep,
  recipientSummary,
  type Recipient,
} from '@/features/checkout-recipient/client';
import { PickupPointPicker } from '@/features/pickup-point-picker/client';
import { Breadcrumbs } from '@/shared/ui/breadcrumbs';
import { ButtonLink } from '@/shared/ui/button';
import { Icon } from '@/shared/ui/icon';
import { StatusScreen } from '@/shared/ui/status-screen';
import { TextButton, TextLink } from '@/shared/ui/text-link';

import styles from './checkout-flow.module.scss';
import { OrderSummary } from './order-summary';

type Step = 'recipient' | 'delivery' | 'payment';

export type CheckoutPrefillData = {
  name: string;
  phone: string;
  email: string;
  city: string | null;
  address: string | null;
};

export type CheckoutFlowProps = {
  paymentMethods: PaymentMethod[];
  prefill: CheckoutPrefillData | null;
  showDemoNotes: boolean;
};

function Collapsed({
  number,
  title,
  onEdit,
  children,
}: {
  number: string;
  title: string;
  onEdit: () => void;
  children: ReactNode;
}) {
  return (
    <div className={styles.step}>
      <span className={styles.number}>{number}</span>
      <div className={styles.collapsed}>
        <div className={styles['collapsed-head']}>
          <span className={styles['step-title']}>{title}</span>
          <TextButton onClick={onEdit}>Изменить</TextButton>
        </div>
        {children}
      </div>
    </div>
  );
}

/** Checkout (05B): recipient → delivery → payment. Totals and availability are re-checked by the server. */
export function CheckoutFlow({ paymentMethods, prefill, showDemoNotes }: CheckoutFlowProps) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const cart = useQuery(cartQueryOptions());
  const complete = Boolean(prefill && prefill.name && prefill.phone && prefill.email);
  const [recipient, setRecipient] = useState<Recipient>({
    name: prefill?.name ?? '',
    phone: prefill?.phone ?? '',
    email: prefill?.email ?? '',
  });
  const [step, setStep] = useState<Step>(complete ? 'delivery' : 'recipient');
  // Design 05B starts with a pickup point; a saved address is ready when the buyer switches to the courier.
  const [chosen, setDraft] = useState<DeliveryDraft>({
    city: prefill?.city ?? 'Москва',
    method: 'pickup',
    point: null,
    address: prefill?.address ?? '',
    comment: '',
  });
  const [tip, setTip] = useState('');
  const [mapOpen, setMapOpen] = useState(false);
  const firstAvailable = paymentMethods.find((method) => method.available)?.id ?? 'card';
  const [payment, setPayment] = useState<PaymentMethodId>(firstAvailable);
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const city = useDeferredValue(chosen.city);
  const options = useQuery({
    ...deliveryOptionsQueryOptions(city),
    enabled: city.trim().length >= 2,
  });
  const points = useQuery({
    ...pickupPointsQueryOptions(city),
    enabled: chosen.method === 'pickup' && city.trim().length >= 2,
  });
  // Until the buyer picks a point, the one the delivery service suggests is selected (05B: point 2 in Moscow).
  const draft: DeliveryDraft = {
    ...chosen,
    point: chosen.point ?? points.data?.find((point) => point.preselected) ?? null,
  };
  const option = options.data?.find((item) => item.id === draft.method && item.available) ?? null;

  const order = useMutation({
    mutationKey: ['checkout', 'place-order'],
    mutationFn: () =>
      placeOrder(
        {
          recipient,
          delivery: {
            city: draft.city.trim(),
            method: draft.method,
            pickupPointId: draft.method === 'pickup' ? (draft.point?.id ?? null) : null,
            address: draft.method === 'pickup' ? null : draft.address.trim(),
          },
          comment: draft.comment.trim(),
          paymentMethod: payment,
        },
        idempotencyKey,
      ),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: cartKeys.current() });
      router.push(
        result.next === 'payment' ? `/checkout/pay/${result.orderId}` : `/orders/${result.orderId}`,
      );
    },
  });

  const data = cart.data;
  if (!data || data.lines.length === 0) {
    return (
      <StatusScreen
        title={cart.isPending ? 'Загружаем корзину…' : 'Корзина пуста'}
        actions={
          cart.isPending ? undefined : (
            <ButtonLink href="/catalog" variant="ghost">
              Перейти в каталог
            </ButtonLink>
          )
        }
      >
        {!cart.isPending && <p>Добавьте товары, чтобы оформить заказ.</p>}
      </StatusScreen>
    );
  }

  const goPay = () => {
    if (draft.city.trim().length < 2) {
      setTip('Укажите город');
      return;
    }
    if (!option) {
      setTip('Выберите доступный способ получения');
      return;
    }
    if (draft.method === 'pickup' && !draft.point) {
      setTip('Выберите пункт выдачи на карте');
      return;
    }
    if (draft.method !== 'pickup' && draft.address.trim().length < 5) {
      setTip('Укажите адрес доставки');
      return;
    }
    setTip('');
    setStep('payment');
  };
  const filled = step === 'recipient' ? 1 : step === 'delivery' ? 2 : 3;
  const deliverySummary = [
    draft.city.trim(),
    option?.title ?? '',
    draft.method === 'pickup' && draft.point ? pickupPointLabel(draft.point) : draft.address.trim(),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className={styles.layout}>
      <div className={styles.head}>
        <Breadcrumbs
          items={[
            { label: 'Каталог', href: '/catalog' },
            { label: 'Корзина', href: '/cart' },
            { label: 'Оформление' },
          ]}
        />
        <div className={styles.heading}>
          <h1 className={styles.title}>Оформление</h1>
          <TextLink href="/cart" tone="muted">
            ← В корзину
          </TextLink>
        </div>
        <div className={styles.progress}>
          <progress
            className={styles['progress-native']}
            value={filled}
            max={3}
            aria-label="Этап оформления"
          />
          {[1, 2, 3].map((index) => (
            <span
              key={index}
              className={styles.segment}
              data-on={index <= filled || undefined}
              aria-hidden="true"
            />
          ))}
        </div>
      </div>
      <div className={styles.steps}>
        {step === 'recipient' ? (
          <div className={styles.step}>
            <span className={styles.number}>01</span>
            <div className={styles['step-body']}>
              <h2 className={styles['step-title']}>Получатель</h2>
              <RecipientStep
                initial={recipient}
                onDone={(value) => {
                  setRecipient(value);
                  setStep('delivery');
                }}
              />
            </div>
          </div>
        ) : (
          <Collapsed number="01" title="Получатель" onEdit={() => setStep('recipient')}>
            <span className={styles['summary-line']}>{recipientSummary(recipient)}</span>
            <span className={styles.caption}>
              {showDemoNotes ? 'Шаг заполнен — свёрнут в строку. ' : ''}Статус заказа придёт в SMS,
              регистрация не нужна.
            </span>
          </Collapsed>
        )}

        {step === 'delivery' && (
          <div className={styles.step}>
            <span className={styles.number}>02</span>
            <div className={styles['step-body']}>
              <DeliveryStep
                draft={draft}
                onChange={(next) => {
                  setDraft(next);
                  setTip('');
                }}
                onOpenMap={() => setMapOpen(true)}
                onSubmit={goPay}
                tip={tip}
                pointMeta={pickupPointMeta}
              />
            </div>
          </div>
        )}
        {step === 'payment' && (
          <Collapsed number="02" title="Получение" onEdit={() => setStep('delivery')}>
            <span className={styles['summary-line']}>{deliverySummary}</span>
            {draft.comment.trim() && (
              <span className={styles.caption}>Комментарий: {draft.comment.trim()}</span>
            )}
          </Collapsed>
        )}
        {step === 'recipient' && (
          <div className={styles.row} data-muted>
            <span className={styles.number}>02</span>
            <span className={styles['step-title']}>Получение</span>
          </div>
        )}

        {step === 'payment' ? (
          <div className={styles.step} data-last>
            <span className={styles.number}>03</span>
            <div className={styles['step-body']}>
              <PaymentStep
                methods={paymentMethods}
                value={payment}
                onChange={setPayment}
                pending={order.isPending}
                error={order.error?.message ?? ''}
                onPay={() => order.mutate()}
              />
            </div>
          </div>
        ) : (
          <button
            type="button"
            className={styles.row}
            onClick={step === 'delivery' ? goPay : undefined}
            aria-expanded="false"
            aria-disabled={step !== 'delivery' || undefined}
          >
            <span className={styles.number}>03</span>
            <span className={styles['step-title']}>Оплата</span>
            <Icon name="chevron-down" size={22} strokeWidth={1.3} />
          </button>
        )}
      </div>
      <OrderSummary
        cart={data}
        deliveryPrice={step === 'recipient' ? null : (option?.price ?? null)}
      />
      <PickupPointPicker
        open={mapOpen}
        onOpenChange={setMapOpen}
        city={draft.city}
        onCityChange={(next) => setDraft({ ...draft, city: next, point: null })}
        selectedId={draft.point?.id ?? null}
        onConfirm={(point) => {
          setDraft({ ...draft, method: 'pickup', point });
          setMapOpen(false);
          setTip('');
        }}
      />
    </div>
  );
}
